/**
 * SnapshotViewBanner — persistent top banner shown whenever the app is
 * rendering a decrypted snapshot in read-only "View Mode".
 *
 * Exports two components:
 *   - `SnapshotViewBannerHost`: wraps the app, renders the banner at the top
 *     when view-mode is active, and renders its children in
 *     `BelowBannerSafeArea` so screens below see a top inset of 0 and don't
 *     double-pad.
 *   - `SnapshotViewBanner` (default): just the bar itself — used by the host.
 *
 * The host sits above `NavigationContainer` so the banner remains visible
 * across every screen while a snapshot is open. Tapping "Exit" clears
 * view-mode and re-fetches live data for the project.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import { useTheme } from '../hooks/useTheme';
import { useAppDispatch, type RootState } from '../store';
import Icon from './Icon';
import BelowBannerSafeArea from './BelowBannerSafeArea';
import { exitSnapshotView } from '../store/actions/snapshots';
import type { AppTheme } from '../theme';

const BANNER_BG = '#B45309';

function formatViewingDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

// ─── Banner bar ───────────────────────────────────────────────────────────────

function SnapshotViewBanner() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const viewMode = useSelector((s: RootState) => s.snapshots?.viewMode ?? null);
  const [exiting, setExiting] = useState(false);

  const styles = useMemo(() => makeStyles(theme), [theme]);

  const handleExit = useCallback(async () => {
    if (!viewMode || exiting) return;
    setExiting(true);
    try {
      await dispatch(exitSnapshotView(viewMode.project_id) as any);
    } finally {
      setExiting(false);
    }
  }, [dispatch, viewMode, exiting]);

  if (!viewMode) return null;

  const dateLabel = formatViewingDate(viewMode.created_at);

  return (
    <SafeAreaView edges={['top']} style={styles.safeArea}>
      <View style={styles.bar}>
        <View style={styles.iconWrap}>
          <Icon icon={['fad', 'eye']} size={14} color="#fff" />
        </View>
        <View style={styles.textWrap}>
          <Text style={styles.title} numberOfLines={1}>
            Viewing snapshot · v{viewMode.version}
          </Text>
          {!!dateLabel && (
            <Text style={styles.subtitle} numberOfLines={1}>
              Read-only preview from {dateLabel}
            </Text>
          )}
        </View>
        <TouchableOpacity
          style={styles.exitBtn}
          onPress={handleExit}
          activeOpacity={0.8}
          disabled={exiting}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          {exiting ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Text style={styles.exitBtnText}>Exit</Text>
              <Icon icon={['fad', 'arrow-right-from-bracket']} size={11} color="#fff" />
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

// ─── Host ─────────────────────────────────────────────────────────────────────

interface HostProps {
  children: React.ReactNode;
}

/**
 * Renders the banner above children when view-mode is active. The banner
 * absorbs the status bar, so children render in `BelowBannerSafeArea`, which
 * gives them a top inset of 0 on every platform.
 *
 * Earlier revisions overrode only `SafeAreaInsetsContext`, and before that
 * also pulled the children up by `-insets.top`. The negative margin covered
 * the banner and was removed; the context override alone never reached
 * `react-native-safe-area-context`'s `SafeAreaView` on iOS/Android, which is
 * a native view that reads its nearest native provider, not the context.
 */
export function SnapshotViewBannerHost({ children }: HostProps) {
  const viewMode = useSelector((s: RootState) => s.snapshots?.viewMode ?? null);

  if (!viewMode) return <>{children}</>;

  return (
    <View style={hostStyles.root}>
      <SnapshotViewBanner />
      <BelowBannerSafeArea>{children}</BelowBannerSafeArea>
    </View>
  );
}

export default SnapshotViewBanner;

// ─── Styles ───────────────────────────────────────────────────────────────────

const hostStyles = StyleSheet.create({
  root: { flex: 1 },
});

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    safeArea: {
      backgroundColor: BANNER_BG,
    },
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: 8,
      backgroundColor: BANNER_BG,
    },
    iconWrap: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: 'rgba(255,255,255,0.18)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    textWrap: { flex: 1 },
    title: {
      color: '#fff',
      fontSize: theme.font.size.sm,
      fontWeight: theme.font.weight.semibold,
    },
    subtitle: {
      color: 'rgba(255,255,255,0.82)',
      fontSize: theme.font.size.xs,
      marginTop: 1,
    },
    exitBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: theme.radius.full,
      backgroundColor: 'rgba(0,0,0,0.2)',
    },
    exitBtnText: {
      color: '#fff',
      fontSize: theme.font.size.xs,
      fontWeight: theme.font.weight.semibold,
    },
  });
